<template>
    <li>
        <general-info
            :record="project"
            :editable="can_edit_prospective_data"
            :wizard-shown="wizardShown"
        />

        <requests-for-material
            :record="project"
            :can-add="can_edit_prospective_data"
            :editable="can_edit_cards"
            :wizard-shown="wizardShown"
        />

        <div class="lam-page-graph">
            <!--
            <div class="uk-button-group lam-graph-filter uk-text-center">
                <base-button
                    size="mini"
                    :class="{'uk-active': chart_scope==='quarter'}"
                    @click.prevent="chart_scope='quarter'"
                >
                    12 Weeks
                </base-button>
                <base-button
                    size="mini"
                    :class="{'uk-active': chart_scope==='remaining'}"
                    @click.prevent="chart_scope='remaining'"
                >
                    Remaining
                </base-button>
                <base-button
                    size="mini"
                    :class="{'uk-active': chart_scope==='project'}"
                    @click.prevent="chart_scope='project'"
                >
                    Whole Project
                </base-button>
            </div>
            //-->
            <div class="uk-grid uk-grid-small uk-margin-bottom">
                <div class="uk-width-1-1">
                    <h4 class="uk-text-center uk-margin-bottom-remove uk-clearfix">
                        Projected Hours

                        <div v-if="editable && !!project && !!project.computed_starts_at" class="uk-float-right">
                            <base-button
                                size="small"
                                class="uk-margin-bottom"
                                @click.prevent="offsetting = true"
                            >
                                <open-icon glyph="fast-forward" />
                            </base-button>
                        </div>
                    </h4>
                    <div>
                        <projected-hours-graph
                            :chart-data="projected_hours_chart_data"
                            style="height: 300px;"
                        />
                    </div>
                </div>
            </div>
        </div>

        <phases
            :record="project"
            :editable="can_edit_prospective_data"
            :wizard-shown="wizardShown"
        />

        <offset-dialog
            v-if="!!project"
            :show.sync="offsetting"
            :record="project"
        />
    </li>
</template>

<script type="text/javascript">
import { mapGetters } from "vuex"

import tab from "../mixins/Tab"
import wizard_target from "../mixins/WizardTarget"
import phase_utilities from "../../../mixins/PhaseUtilities"
import uses_color_palette from "../../../mixins/UsesColorPalette"

import GeneralInfo from "./GeneralInfo"
import RequestsForMaterial from "./RequestsForMaterial"
import Phases from "./Phases"
import ProjectedHoursGraph from "./ProjectedHoursGraph.js"
import BaseButton from "../../../ui/BaseButton"
import OpenIcon from "../../../ui/OpenIcon"
import OffsetDialog from "../../../dialogs/OffsetDialog"

export default {
    mixins: [tab, wizard_target, phase_utilities, uses_color_palette],
    components: {
        GeneralInfo,
        RequestsForMaterial,
        Phases,
        ProjectedHoursGraph,
        BaseButton,
        OpenIcon,
        OffsetDialog
    },
    computed: {
        ...mapGetters(["entity_records"]),
        phases () {
            if(!this.project || !this.project.id) return []
            return this.entity_records("phase").filter(phase => phase.project_id === this.project.id)
        },
        can_edit_cards () {
            if(this.is_db_admin_active) return true
            else if(!this.project) return false
            if(this.is_active_project) return this.is_overhead_project || (this.editable && this.im_pic_or_pm) || this.is_administrator || this.is_business_manager
            return this.can_edit_prospective_data
        },
        projected_hours_chart_data () {
            const data = {
                labels: [],
                datasets: [
                    /*
                    {
                        label: "Projected Hours",
                        backgroundColor: this.color_active.background,
                        borderColor: this.color_active.background,
                        categoryPercentage: 0.9,
                        barPercentage: 0.8,
                        data: []
                    }
                    */
                ]
            }
            const phase_datasets = {}
            this.phases.forEach((phase, index) => {
                phase_datasets[phase.id] = {
                    label: this.phase_standardized_title(phase),
                    backgroundColor: phase.hex_color,
                    borderColor: phase.hex_color,
                    categoryPercentage: 0.9,
                    barPercentage: 0.8,
                    data: []
                }
            })

            const inflate_data = (date, end, label_format = "MMM Do", end_of = "isoWeek", add = 1, add_units = "weeks") => {
                let data_index = -1

                while(date.isSameOrBefore(end)) {
                    data_index++

                    const next_date = this.$moment(date).add(add, add_units)
                    let label = date.format(label_format)
                    if(!data_index || (next_date.isAfter(end)) || (date.week() === 1)) label = date.format(`${label_format} YYYY`)
                    data.labels.push(label)

                    const to = this.$moment(date).add(add, add_units).subtract(1, "day").endOf(end_of)
                    this.phases.forEach(phase => {
                        let contributes = false
                        const phase_starts = this.$moment(phase.starts_at).startOf("day")
                        const phase_ends = this.$moment(phase.ends_at).endOf("day")

                        if(date.isSameOrAfter(phase_starts) && to.isSameOrBefore(phase_ends)) contributes = true
                        else if(phase_starts.isSameOrAfter(date) && phase_starts.endOf("day").isSameOrBefore(to)) contributes = true
                        else if(phase_ends.isSameOrBefore(to) && phase_ends.startOf("day").isSameOrAfter(date)) contributes = true

                        phase_datasets[phase.id].data.push(contributes ? phase.computed_hours_per_week : 0)
                    })

                    date.add(add, add_units)
                }
            }

            switch (this.chart_scope) {
            /*
                case "quarter": // 12 weeks out, as of this week
                    let end = this.$moment().endOf('day').add(12, 'weeks')
                    inflate_data(this.$moment().startOf('isoWeek'), end)
                break;

                case "remaining": // everything left as of today
                    inflate_data(this.$moment().startOf('isoWeek'), this.$moment(this.project.computed_ends_at).endOf('isoWeek'))
                break;
                */
            default: // project: the whole project from start to finish
                inflate_data(this.$moment(this.project.computed_starts_at).startOf("isoWeek"), this.$moment(this.project.computed_ends_at).endOf("isoWeek"))
                break
            }

            data.datasets = Object.values(phase_datasets)

            return data
        }
    },
    data () {
        return {
            chart_scope: "project", // "quarter", "remaining" or "project"
            offsetting: false
        }
    }
}
</script>
